import React, { useState, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import File from '@peracto/peracto-editor-ui/dist/File'
import Select from '@peracto/peracto-editor-ui/dist/Select'
import { useSettings } from '@peracto/peracto-hooks'

import debounce from 'debounce-promise'
import startCase from 'lodash/startCase'
import uniqBy from 'lodash/uniqBy'
import axios from 'axios'

import { useConfig } from '@peracto/peracto-config'

const MenuItemsCarouselForm = ({
    onChange,
    state: {
        menu = {},
        menuItem = {},
        cardStyle = 'square',
        progressIndicatorStyle = 'dots',
        imageFit = 'cover',
        fontFamily = '',
        fontWeight = 'normal',
        fontColour = '#000',
        accentColour = '#000',
        arrowColour = '#FFFFFF',
        fallbackImage,
    },
}) => {
    const [parentMenu, setParentMenu] = useState(menu)

    const { values: p_values } = useSettings()
    const configCategoryPath = p_values?.sub_urls?.sitemap_categories_sub_url || 'category'

    const config = useConfig()
    const API_URL = config.get('api')

    const { theme, fonts, colorOptions } = config.get('editorConfig') || []

    const themeColours = []
    const themeFonts = []
    const themeFontWeights = [
        { label: 'Light', value: 'light' },
        { label: 'Normal', value: 'normal' },
        { label: 'Bold', value: 'bold' },
    ]

    if (colorOptions) {
        // Tailwind
        themeColours.push(...colorOptions)
    } else if (theme?.colors?.brand) {
        // Editor theme
        for (const [label, colour] of Object.entries(theme.colors.brand)) {
            themeColours.push({
                label: startCase(label),
                value: colour,
            })
        }
    }

    if (fonts) {
        // Tailwind
        for (const [label, font] of Object.entries(fonts)) {
            themeFonts.push({
                label: startCase(label),
                value: Array.isArray(font) ? font[0] : font,
            })
        }
    } else if (theme?.fonts) {
        // Editor theme
        for (const [label, font] of Object.entries(theme.fonts)) {
            themeFonts.push({
                label: startCase(label),
                value: font,
            })
        }
    }

    const fetchMenus = async input => {
        const { data } = await axios.get(`${API_URL}/menus?name=${input || ''}`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        })

        const parsedData = data['hydra:member']

        const values = parsedData.map(menu => ({
            label: menu.name,
            value: `/menus/${menu.code}`,
        }))

        return values
    }

    const debouncedFetchMenus = debounce(fetchMenus, 200)

    let menuItems = []

    const formatMenuItems = menu => {
        menu.forEach(item => {
            menuItems.push({
                label: item.linkText,
                value: item.path,
            })

            if (item?.children?.length > 0) {
                formatMenuItems(item.children)
            }
        })
        return menuItems
    }

    const fetchMenuItems = async input => {
        menuItems = []

        const { data } = await axios.get(`${API_URL}${parentMenu.value}`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        })

        const values = formatMenuItems(data.menuItems)

        return values.filter(val => val.label.toLowerCase().includes(input.toLowerCase()))
    }

    const debouncedFetchMenuItems = debounce(fetchMenuItems, 200)

    useEffect(() => {
        onChange({ categoryPath: configCategoryPath })
    }, [configCategoryPath])

    return (
        <>
            <div className="form-group">
                <label>Menu</label>
                <AsyncSelect
                    className="!w-full"
                    loadOptions={input => debouncedFetchMenus(input)}
                    isSearchable={true}
                    defaultOptions={true}
                    value={menu}
                    onChange={option => {
                        setParentMenu(option)
                        onChange({ menu: option })
                        onChange({ menuItem: {} })
                    }}
                    placeholder="Select a menu..."
                    classNamePrefix="peracto-select"
                    noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length > 0) {
                            return `No menus found for '${inputValue}'.`
                        } else {
                            return 'Enter text to begin searching.'
                        }
                    }}
                />
            </div>

            <div className="form-group">
                <label>Menu Item</label>
                <AsyncSelect
                    className="!w-full"
                    loadOptions={input => debouncedFetchMenuItems(input)}
                    isSearchable={true}
                    value={menuItem}
                    onChange={option => {
                        onChange({ menuItem: option })
                        onChange({ menuItemPath: option.value })
                    }}
                    isDisabled={!parentMenu}
                    placeholder="Select a menu item..."
                    classNamePrefix="peracto-select"
                    noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length > 0) {
                            return `No menus found for '${inputValue}'.`
                        } else {
                            return 'Enter text to begin searching.'
                        }
                    }}
                />
            </div>

            <Select
                label="Card Style"
                options={[
                    { label: 'Square', value: 'square' },
                    { label: 'Rectangle', value: 'rectangle' },
                ]}
                value={cardStyle}
                onChange={e => onChange({ cardStyle: e.value })}
            />

            <Select
                label="Progress Indicator Style"
                options={[
                    { label: 'Dots', value: 'dots' },
                    // { label: 'Bar', value: 'bar' },
                ]}
                defaultValue={{ label: 'Dots', value: 'dots' }}
                onChange={e => onChange({ progressIndicatorStyle: e.value })}
            />

            <Select
                label="Image Fit"
                options={[
                    { label: 'Cover', value: 'cover' },
                    { label: 'Contain', value: 'contain' },
                ]}
                value={imageFit}
                onChange={e => onChange({ imageFit: e.value })}
            />

            <File
                label="Fallback Image"
                value={fallbackImage}
                onChange={value => onChange({ fallbackImage: value })}
            />

            <Select
                label="Font Family"
                options={uniqBy(themeFonts, fontFamily => fontFamily.value)}
                value={fontFamily}
                onChange={e => onChange({ fontFamily: e.value })}
            />

            <Select
                label="Font Weight"
                options={themeFontWeights}
                value={fontWeight}
                onChange={e => onChange({ fontWeight: e.value })}
            />

            <Select
                label="Font Colour"
                options={themeColours}
                value={fontColour}
                onChange={e => onChange({ fontColour: e.value })}
            />

            <Select
                label="Accent Colour"
                options={themeColours}
                value={accentColour}
                onChange={e => onChange({ accentColour: e.value })}
            />

            <Select
                label="Arrow Colour"
                options={themeColours}
                value={arrowColour}
                onChange={e => onChange({ arrowColour: e.value })}
            />
        </>
    )
}

export default MenuItemsCarouselForm
